import React, { useCallback } from 'react';

import { useScreenState } from '@gogame/screens';
import { useChildComponent } from '@gogame/hooks-ts';

import { SubscriptionCreate } from 'App/components/Subscriptions/SubscriptionsForm';
import RequestMail from 'App/components/Subscriptions/components/RequestMailDialog';
import { useGoogleDrive } from 'api/google/useGoogleDrive';

import {
  useTrackerChart,
  useLoadAnnotations,
  useLoadChartMetrics,
} from 'scenes/Trackers/Tracker/data';
import {
  useExportToGoogleDriveChart,
  useExportTrackerChart,
  useRequestMail,
} from 'scenes/Trackers/Tracker/data/mutations';
import { useCurrentDimension } from 'scenes/Trackers/hooks';
import { TRACKER_CHART_PAGE } from 'scenes/Trackers/constants';

import Chart, {
  useChartPayload,
  useChartState,
  useCanLoadAnnotations,
} from './Chart';
import {
  scaleOptions,
  depositsAggregationOptions,
  changeRateOptions,
  weekStartOptions,
  lookbackOptions,
  outputOptions,
} from './Chart/Actions/ChartSettings/constants';

window.dataLayer = window.dataLayer || [];

const TrackerChart = ({ presetName, ...props }) => {
  const chartState = useChartState();
  const screenState = useScreenState();
  const dimension = useCurrentDimension();

  const isAnnotationsEnabled = useCanLoadAnnotations();

  const chartPayload = useChartPayload();
  const [exportTrackerChart] = useExportTrackerChart();
  const [exportToGD] = useExportToGoogleDriveChart(chartPayload);
  const [requestMail] = useRequestMail();

  const [childComponent, showChildComponent] = useChildComponent();

  const {
    data: chartSeries,
    isLoading,
    isFetching,
  } = useTrackerChart(chartPayload, () => {
    window.dataLayer.push({
      event: 'loadChart',
      ...getSettingsView(chartPayload.settings),
    });
  });
  const { data: annotations, isLoading: isAnnotationsLoading } =
    useLoadAnnotations({
      filters: screenState.filtersCollection[0],
      annotationsSettings: chartState.annotations,
      enabled: isAnnotationsEnabled,
    });
  const { data: metrics, isLoading: isMetricsLoading } = useLoadChartMetrics({
    dimension,
  });

  const handleExport = () => {
    exportTrackerChart(chartPayload);
  };

  const handleExportGoogleDrive = useCallback(
    ({ token }) => {
      return exportToGD({ accessToken: token });
    },
    [screenState]
  );

  const { onExport: onExportGoogleDrive } = useGoogleDrive({
    onGetToken: handleExportGoogleDrive,
  });

  const handleExportWhenReady = () => {
    showChildComponent(RequestMail, {
      onSubmit: ({ subject }) => {
        requestMail({
          page: TRACKER_CHART_PAGE,
          payload: chartPayload,
          name: subject,
        });
      },
      subject: `[${presetName || `GG Tracker`}] Your report is ready`,
    });
  };

  const handleOpenSubscriptionsForm = () => {
    showChildComponent(SubscriptionCreate, {
      page: TRACKER_CHART_PAGE,
      payload: chartPayload,
    });
  };

  return (
    <React.Fragment>
      <Chart
        {...props}
        chartSeries={chartSeries}
        annotations={annotations}
        metrics={metrics}
        isLoading={isLoading || isFetching}
        isAnnotationsLoading={isAnnotationsLoading}
        isMetricsLoading={isMetricsLoading}
        onChangeViewSettings={(viewSettings) => {
          window.dataLayer.push({
            event: 'changeChartViewSettings',
            viewSettingsBrush: viewSettings.brush,
            viewSettingsLabel: viewSettings.label,
            viewSettingsLegend: viewSettings.legend,
            viewSettingsPercentage: viewSettings.percentage,
            viewSettingsType: viewSettings.type,
          });
        }}
        onExport={handleExport}
        onExportGoogleDrive={onExportGoogleDrive()}
        onExportWhenReady={handleExportWhenReady}
        onOpenSubscriptionsForm={handleOpenSubscriptionsForm}
      />
      {childComponent}
    </React.Fragment>
  );
};

function getLabel(lookup, id) {
  return lookup.find((opt) => opt.id === id)?.name;
}

function getSettingsView(settings) {
  return {
    settingsChangeRateMode: getLabel(
      changeRateOptions,
      settings.changeRateMode
    ),
    settingsChartGrouping: getLabel(
      depositsAggregationOptions,
      settings.chartGrouping
    ),
    settingsIncludeChangeRate: settings.includeChangeRate,
    settingsLookBackMode: getLabel(lookbackOptions, settings.lookbackMode),
    settingsOutput: getLabel(outputOptions, settings.output),
    settingsScale: getLabel(scaleOptions, settings.scale),
    settingsScaleWeekStart: getLabel(weekStartOptions, settings.scaleWeekStart),
    settingsTrend: settings.trend,
  };
}

export default TrackerChart;
